import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

const CATEGORIES = ["Participant", "Intern", "Volunteer", "Trainee", "Programme Participant", "Other"];

export default function Register() {
  const { t } = useApp();
  const [form, setForm] = useState({ category: "Participant" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (photo) fd.append("photo", photo);
      const r = await api.post("/public/participants", fd);
      setDone(r.data);
      toast.success(t("reg_success"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div key={key}>
      <label htmlFor={`reg-${key}`} className="mb-1.5 block text-sm font-semibold text-navy">{label}</label>
      <input id={`reg-${key}`} data-testid={`reg-${key}`} value={form[key] || ""} onChange={set(key)}
        className="w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-saffron-500/50" {...props} />
    </div>
  );

  if (done) {
    return (
      <div data-testid="register-success">
        <PageHero title={t("participant_reg")} />
        <section className="py-20">
          <div className="container-ssf max-w-xl">
            <div className="card-ssf p-10 text-center">
              <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
              <h2 className="mt-4 font-display text-2xl font-bold text-navy">{t("reg_success")}</h2>
              <p className="mt-3 text-sm text-slate-600">Registration Number</p>
              <p className="mt-1 font-display text-3xl font-bold text-saffron-600" data-testid="reg-number">{done.reg_no}</p>
              {!done.email_sent && <p className="mt-4 text-xs text-slate-500">Email notification not configured — please note your registration number.</p>}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="register-page">
      <SEO title={t("participant_reg")} description="Register as a participant, intern, volunteer or trainee with Sahaj Sarathi Foundation." />
      <PageHero title={t("participant_reg")} sub="Join our programmes as a participant, intern, volunteer or trainee. Fields marked optional may be left blank." />
      <section className="py-16">
        <div className="container-ssf max-w-3xl">
          <form onSubmit={submit} className="card-ssf space-y-5 p-8" data-testid="registration-form">
            <div>
              <label htmlFor="reg-category" className="mb-1.5 block text-sm font-semibold text-navy">{t("category")} *</label>
              <select id="reg-category" data-testid="reg-category" value={form.category} onChange={set("category")}
                className="w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {field("full_name", `${t("full_name")} *`, { required: true })}
              {field("guardian_name", t("guardian"))}
              {field("dob", t("dob"), { type: "date" })}
              <div>
                <label htmlFor="reg-gender" className="mb-1.5 block text-sm font-semibold text-navy">{t("gender")}</label>
                <select id="reg-gender" data-testid="reg-gender" value={form.gender || ""} onChange={set("gender")}
                  className="w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50">
                  <option value="">—</option><option value="Male">{t("male")}</option><option value="Female">{t("female")}</option><option value="Other">{t("other")}</option>
                </select>
              </div>
              {field("mobile", t("mobile"), { type: "tel" })}
              {field("whatsapp", t("whatsapp"), { type: "tel" })}
              {field("email", t("email"), { type: "email" })}
              {field("programme", t("programme"))}
              {field("course", t("course"))}
              {field("joining_date", t("joining_date"), { type: "date" })}
            </div>
            {field("address", t("address"))}
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {field("city", t("city"))}
              {field("district", t("district"))}
              {field("state", t("state"))}
              {field("pin", t("pin"))}
            </div>
            <div>
              <label htmlFor="reg-photo" className="mb-1.5 block text-sm font-semibold text-navy">{t("photo")}</label>
              <input id="reg-photo" data-testid="reg-photo" type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-saffron-100 file:px-4 file:py-1.5 file:text-xs file:font-bold file:text-saffron-600" />
            </div>
            <div>
              <label htmlFor="reg-remarks" className="mb-1.5 block text-sm font-semibold text-navy">{t("remarks")}</label>
              <textarea id="reg-remarks" data-testid="reg-remarks" rows={3} value={form.remarks || ""} onChange={set("remarks")}
                className="w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="registration-submit">
              {busy ? t("loading") : t("submit_registration")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
